import { InjectionToken } from '@angular/core';
import {
  createSelector,
  createFeatureSelector,
  ActionReducer,
  Action,
  ActionReducerMap,
  MetaReducer
} from '@ngrx/store';
import { localStorageSync } from 'ngrx-store-localstorage';

import * as auth from '@reducers/auth/auth.reducer';
import * as router from '@reducers/router.reducer';
import * as contacts from '@reducers/contacts/contacts.reducer';
import * as users from '@reducers/users/users.reducer';
import * as locations from '@reducers/locations/locations.reducer';
import * as business from '@reducers/business/business.reducer';
import * as reviewTargets from '@reducers/review-targets/review-targets.reducer';
import * as admin from '@reducers/admin.reducer';
import * as campaigns from '@reducers/campaigns/campaigns.reducer';
import * as reviews from '@reducers/reviews/reviews.reducer';
import * as feedbacks from '@reducers/feedbacks/feedbacks.reducer';
import * as contactHistory from '@reducers/contactHistory/contactHistory.reducer';
import * as widgets from '@reducers/widgets/widgets.reducer';
import * as dashboards from '@reducers/dashboards/dashboards.reducer';
import * as reports from '@reducers/reports/reports.reducer';
import * as gmb from '@reducers/gmb/gmb.reducer';
import * as facebook from '@reducers/facebook/facebook.reducer';
import * as app from '@reducers/app.reducer';
import * as replyTemplate from '@reducers/reply-template.reducer';

import { filterByQuery } from '@utilities/function-utils';
import { businessPropertiesToCheck, resellerPropertiesToCheck } from '@utilities/const-utils'
import { ResellerModel, BusinessModel, LocationModel } from '@models/index';

export interface State {
  router: router.State;
  auth: auth.State;
  //home: home.State;
  contacts: contacts.State;
  users: users.State;
  locations: locations.State;
  business: business.State;
  reviewTargets: reviewTargets.State;
  admin: admin.State;
  campaigns: campaigns.State;
  reviews: reviews.State;
  feedbacks: feedbacks.State;
  contactHistory: contactHistory.State;
  /**
   * widgets: Stores the widget's app state
   */
  widgets: widgets.State;
  dashboards: dashboards.State;
  reports: reports.State;
  gmb: gmb.State;
  facebook: facebook.State;
  app: app.State,
  replyTemplate: replyTemplate.State
}

export const ROOT_REDUCERS = new InjectionToken<
  ActionReducerMap<State, Action>
>('Root reducers token', {
  factory: () => ({
    router: router.reducer,
    auth: auth.reducer,
    //home: home.reducer,
    contacts: contacts.reducer,
    users: users.reducer,
    locations: locations.reducer,
    business: business.reducer,
    reviewTargets: reviewTargets.reducer,
    admin: admin.reducer,
    campaigns: campaigns.reducer,
    reviews: reviews.reducer,
    feedbacks: feedbacks.reducer,
    contactHistory: contactHistory.reducer,
    /**
     * Widget's reducer method
     */
    widgets: widgets.reducer,
    dashboards: dashboards.reducer,
    reports: reports.reducer,
    gmb: gmb.reducer,
    facebook: facebook.reducer,
    app: app.reducer,
    replyTemplate: replyTemplate.reducer
  }),
});

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({
    keys: [
      'auth',
      'users',
      'locations',
      'business',
      'resellers',
      'admin',
      'dashboards',
      'gmb',
      'facebook',
      'router',
      'replyTemplate',
      'reviewTargets'
    ], rehydrate: true
  })(reducer);
}
export const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

/**
 * Router Reducers
 */
export const getRouterState = createFeatureSelector<State, router.State>(
  'router'
);
export const getCurrentRoute = createSelector(
  getRouterState,
  router.getCurrentRoute
);
/**
 * Auth Reducers
 */
export const getAuthState = createFeatureSelector<State, auth.State>(
  'auth'
);

export const getLoggedUser = createSelector(
  getAuthState,
  auth.getLoggedUser
);
export const getRoleLoggedUser = createSelector(
  getAuthState,
  auth.getRoleLoggedUser
);
export const getIsExecutedTaskAuth = createSelector(
  getAuthState,
  auth.getIsExecutedTask
);
export const getPswChanged = createSelector(
  getAuthState,
  auth.getPswChanged
);
export const getErrorAuth = createSelector(
  getAuthState,
  auth.getError
);
export const getSelectedReseller = createSelector(
  getAuthState,
  auth.getSelectedReseller
);
export const getLocationMetadata = createSelector(
  getAuthState,
  auth.getLocationMetadata
);

/**
 * Home Reducers
 */
/*export const getAuthState = createFeatureSelector<State, auth.State>(
  'auth'
);
 
export const getLoggedUser= createSelector(
  getAuthState,
  auth.getLoggedUser
);*/

/**
* Contacts Reducers
*/
export const getContactsState = createFeatureSelector<State, contacts.State>(
  'contacts'
);

export const getErrorContact = createSelector(
  getContactsState,
  contacts.getError
);

export const getContacts = createSelector(
  getContactsState,
  contacts.getContacts
);

export const getContactAdded = createSelector(
  getContactsState,
  contacts.getContactAdded
);

export const getTotalContact = createSelector(
  getContactsState,
  contacts.getTotal
)

export const isBulkAdded = createSelector(
  getContactsState,
  contacts.isBulkAdded
)

export const getResultBulkAdded = createSelector(
  getContactsState,
  contacts.resultBulkAdd
)
export const isContactExport = createSelector(
  getContactsState,
  contacts.isContactExport
)

export const getExportDataContactCount = createSelector(
  getContactsState,
  contacts.exportDataContactCount
)
/**
 * Users Reducers
 */
export const getUsersState = createFeatureSelector<State, users.State>(
  'users'
);

export const getErrorUsers = createSelector(
  getUsersState,
  users.getError
);

export const getUsers = createSelector(
  getUsersState,
  users.getUsers
);

export const getUserRoles = createSelector(
  getUsersState,
  users.getUserRoles
);

export const getUsersByQuery = createSelector(
  getUsersState,
  users.getFoundUsersByQuery
);

export const getSentInvitations = createSelector(
  getUsersState,
  users.getSentInvitations
)

export const getResentInvitation = createSelector(
  getUsersState,
  users.getResentInvitation
)

export const getSentInvitationStates = createSelector(
  getUsersState,
  users.getSentInvitationStates
)
export const getUserSize = createSelector(
  getUsersState,
  users.getSize
)

/**
 * Locations Reducers
 */
export const getLocationsState = createFeatureSelector<State, locations.State>(
  'locations'
);

export const getLocations = createSelector(
  getLocationsState,
  locations.getLocations
);

export const getLocationsPage = createSelector(
  getLocationsState,
  locations.getPage
);

export const getArchivedLocations = createSelector(
  getLocationsState,
  locations.getArchivedLocations
);

export const getCurrentLocation = createSelector(
  getLocationsState,
  locations.getCurrentLocation
);

export const getExistShortName = createSelector(
  getLocationsState,
  locations.getExistShortName
);

export const getLocationTotal = createSelector(
  getLocationsState,
  locations.getTotal
);

export const getLocationUrls = createSelector(
  getLocationsState,
  locations.getUrl
);

export const getErrorLocations = createSelector(
  getLocationsState,
  locations.getError
);
export const getReviewsLast14Days = createSelector(
  getLocationsState,
  locations.getReviewsLast14Days
);

export const getContactsLast14Days = createSelector(
  getLocationsState,
  locations.getContactsLast14Days
);

export const isLocationCreated = createSelector(
  getLocationsState,
  locations.isLocationCreated
);

export const isSearchingLocations = createSelector(
  getLocationsState,
  locations.isSearchingLocations,
);

export const isSearchingBusinessAndLocation = createSelector(
  getLocationsState,
  locations.isSearchingBusinessAndLocation
);

/**
 * Business Reducers
 */

export const getBusinessState = createFeatureSelector<State, business.State>(
  'business'
);

export const getIsExecutedTaskBusiness = createSelector(
  getBusinessState,
  business.getIsTaskExecuting
);

export const getBusinesses = createSelector(
  getBusinessState,
  business.getBusinesses
)

export const getBusinessByQuery = createSelector(
  getBusinessState,
  business.getSearchBusinessQuery
)
export const getTotalBusinesses = createSelector(
  getBusinessState,
  business.getTotalBusinesses
)
export const getBusinessLocations = createSelector(
  getBusinessState,
  business.getLocations
)
export const getBusinessUsers = createSelector(
  getBusinessState,
  business.getUsers
)
export const getErrorBusinesses = createSelector(
  getBusinessState,
  business.getError
);

export const getSelectedBusinessProfile = createSelector(
  getBusinessState,
  business.getSelectedBusinessProfile
);

export const getContactsAddedOnPeriodOfTime = createSelector(
  getBusinessState,
  business.getContactsAddedOnPeriodOfTime
);

export const getFilterBusinesses = createSelector(
  getBusinesses,
  getBusinessByQuery,
  (businesses, query) => {
    return filterByQuery(businesses, businessPropertiesToCheck, query);
  }
);

export const getBusinessSuccessfullyArchived = createSelector(
  getBusinessState,
  business.getSuccessfullyDeArchived
)

/**
 * Resellers Reducers
 */


export const getAdminState = createFeatureSelector<State, admin.State>(
  'admin'
);

export const getIsExecutedTaskReseller = createSelector(
  getAdminState,
  admin.getIsTaskExecuting
)

export const getResellers = createSelector(
  getAdminState,
  admin.getResellers
)

export const getResellersByQuery = createSelector(
  getAdminState,
  admin.getResellersByQuery
)

export const getErrorResellers = createSelector(
  getAdminState,
  admin.getError
);

export const getSelectedResellerProfile = createSelector(
  getAdminState,
  admin.getSelectedReseller
);

export const getResellerById = createSelector(
  getAdminState,
  admin.getResellerById
);

export const getResellersQuery = createSelector(
  getAdminState,
  admin.getSearchResellerQuery
);

export const getFilterResellers = createSelector(
  getResellers,
  getResellersQuery,
  (resellers, query) => {
    return filterByQuery(resellers, resellerPropertiesToCheck, query);
  }
);

export const getResellerCount = createSelector(
  getAdminState,
  admin.getResellerCount
)

/**
 * Review Targets Reducers
 */


export const getReviewTargetsState = createFeatureSelector<State, reviewTargets.State>(
  'reviewTargets'
);

export const getReviewTargets = createSelector(
  getReviewTargetsState,
  reviewTargets.getReviewTargets
)

export const getUIReviewTargets = createSelector(
  getReviewTargetsState,
  reviewTargets.getUIReviewTargets
)

export const getEnableAllReviewTargets = createSelector(
  getReviewTargetsState,
  reviewTargets.getEnableAll
)

export const getSelectedReviewTarget = createSelector(
  getReviewTargetsState,
  reviewTargets.getSelectedReviewTarget
)

export const getTotalReviewTargets = createSelector(
  getReviewTargetsState,
  reviewTargets.getTotal
)

export const getReviewSourcesByQuery = createSelector(
  getReviewTargetsState,
  reviewTargets.getReviewSourcesByQuery
)


export const getErrorReviewTargets = createSelector(
  getReviewTargetsState,
  reviewTargets.getError
);

export const getLocationReviewTargetWithErrors = createSelector(
  getReviewTargetsState,
  reviewTargets.getLocationReviewTargetWithErrors
);

export const getReviewTargetToIntegrate = createSelector(
  getReviewTargetsState,
  reviewTargets.getReviewTargetToIntegrate
);

export const getShowRevieTargetLocationFormAfterRedirect = createSelector(
  getReviewTargetsState,
  reviewTargets.getShowRevieTargetLocationFormAfterRedirect
);

/**
* Campaigns Reducers
*/


export const getCampaignsState = createFeatureSelector<State, campaigns.State>(
  'campaigns'
);

export const getReviewRequestCampaigns = createSelector(
  getCampaignsState,
  campaigns.getReviewRequestCampaigns
)

export const getSelectedReviewRequestCampaign = createSelector(
  getCampaignsState,
  campaigns.getSelectedReviewRequestCampaign
)
export const getSaveSuccessCampaign = createSelector(
  getCampaignsState,
  campaigns.getSaveSuccess
)
export const getErrorCampaign = createSelector(
  getCampaignsState,
  campaigns.getError
);

export const BasicReviewRequestCampaignInfo = createSelector(
  getCampaignsState,
  campaigns.getLocationCampaignsBasicInfo
)
export const getcampaignsBasicInfoReports = createSelector(
  getCampaignsState,
  campaigns.getcampaignsBasicInfoReports
)

export const getAddSuccessCampaign = createSelector(
  getCampaignsState,
  campaigns.getAddSuccess
)
export const isChangePreferredCampaig = createSelector(
  getCampaignsState,
  campaigns.isChangePreferredCampaig
)


/**
* Reviews Reducers
*/


export const getReviewsState = createFeatureSelector<State, reviews.State>(
  'reviews'
);

export const getReviews = createSelector(
  getReviewsState,
  reviews.getReviews
)

export const getPagesId = createSelector(
  getReviewsState,
  reviews.getPagesId
)

export const getTotalReviews = createSelector(
  getReviewsState,
  reviews.getTotal
)

export const getFilterTotalReviews = createSelector(
  getReviewsState,
  reviews.getFilterTotal
)

export const getReplyToDelete = createSelector(
  getReviewsState,
  reviews.getReplyToDelete
)

export const getReplyToBePosted = createSelector(
  getReviewsState,
  reviews.getReplyToBePosted
)

export const getErrorReviews = createSelector(
  getReviewsState,
  reviews.getError
);

/**
* Feedbacks Reducers
*/


export const getFeedbacksState = createFeatureSelector<State, feedbacks.State>(
  'feedbacks'
);

export const getFeedbacks = createSelector(
  getFeedbacksState,
  feedbacks.getFeedbacks
)

export const getTotalFeedbacks = createSelector(
  getFeedbacksState,
  feedbacks.getTotal
)

export const getFilterTotalFeedbacks = createSelector(
  getFeedbacksState,
  feedbacks.getFilterTotal
)

export const getIsExcecutingFeedbacks = createSelector(
  getFeedbacksState,
  feedbacks.getIsExcecutingTask
)
export const getErrorFeedbacks = createSelector(
  getFeedbacksState,
  feedbacks.getError
);

/**
* Contact History Reducers
*/


export const getContactHistoryState = createFeatureSelector<State, contactHistory.State>(
  'contactHistory'
);

export const getContactHistory = createSelector(
  getContactHistoryState,
  contactHistory.getContactHistory
)

export const getErrorContactHistory = createSelector(
  getContactHistoryState,
  contactHistory.getError
);

/**
* Creates widget feature selector
which points to widget's app state 
*/
export const getWidgetsState = createFeatureSelector<State, widgets.State>(
  'widgets'
);

/**
 * Creates selector to obtain
 * the location's reviews that 
 * match widget's settings from
 * widget's state
 */
export const getWidgetReviews = createSelector(
  getWidgetsState,
  widgets.getReviews
)

/**
 * Creates selector to obtain
 * the ammount of location's
 * reviews that match widget's 
 * settings from widget's state
 */
export const getWidgetReviewsTotal = createSelector(
  getWidgetsState,
  widgets.getTotal
)

/**
 * Creates selector to obtain
 * the widget url
 * from widget's state
 */
export const getWidgetUrl = createSelector(
  getWidgetsState,
  widgets.getUrl
)

/**
 * Creates selector to obtain
 * the ammount of location's
 * reviews from widget's state
 */
export const getWidgetsLoctionReviewsCount = createSelector(
  getWidgetsState,
  widgets.getReviewsCount
)

/**
 * Creates selector to obtain
 * the location's average
 * rating from widget's state
 */
export const getWidgetsLoctionOverallRating = createSelector(
  getWidgetsState,
  widgets.getRating
)

/**
 * Creates selector to obtain
 * the location's stars
 * distribution from widget's
 * state
 */
export const getLoctionStarsCount = createSelector(
  getWidgetsState,
  widgets.getStarsCount
)

/**
 * Creates selector to obtain
 * the location's generic 
 * landing page url from widget's
 * state
 */
export const getLoctionGenericLanding = createSelector(
  getWidgetsState,
  widgets.getLanding
)

/**
 * Creates selector to obtain
 * current widget's error from 
 * widget's state
 */
export const getErrorWidgets = createSelector(
  getWidgetsState,
  widgets.getError
);
export const getPagesIdWidgets = createSelector(
  getWidgetsState,
  widgets.getPagesId
)

/**
* Dashboards Reducers
*/


export const getDashboardsState = createFeatureSelector<State, dashboards.State>(
  'dashboards'
);

export const getBusinessDashboardLocation = createSelector(
  getDashboardsState,
  dashboards.getBusinessDashboardLocation
)

export const getBusinessDashboardReviewsAmount = createSelector(
  getDashboardsState,
  dashboards.getBusinessDashboardReviewsAmount
)

export const getBusinessDashboardFeedbacksAmount = createSelector(
  getDashboardsState,
  dashboards.getBusinessDashboardFeedbacksAmount
)

export const getBusinessDashboardContactsAmount = createSelector(
  getDashboardsState,
  dashboards.getBusinessDashboardContactsAmount
)

export const getBusinessRating = createSelector(
  getDashboardsState,
  dashboards.getBusinessRating
)

export const getBusinessStarsCount = createSelector(
  getDashboardsState,
  dashboards.getBusinessStarsCount
)

export const getBusinessReviewCount = createSelector(
  getDashboardsState,
  dashboards.getBusinessReviewCount
)

export const getBusinessUserCount = createSelector(
  getDashboardsState,
  dashboards.getBusinessUserCount
)

export const getBusinessContactCount = createSelector(
  getDashboardsState,
  dashboards.getBusinessContactCount
)

export const getBusinessLocationCount = createSelector(
  getDashboardsState,
  dashboards.getBusinessLocationCount
)

export const getLocationQueues = createSelector(
  getDashboardsState,
  dashboards.getLocationQueues
)

export const getLocationCampaignMetrics = createSelector(
  getDashboardsState,
  dashboards.getLocationCampaignMetrics
)

export const getLocationTodaySeries = createSelector(
  getDashboardsState,
  dashboards.getLocationTodaySeries
)

export const getLocationLastWeekSeries = createSelector(
  getDashboardsState,
  dashboards.getLocationLastWeekSeries
)

export const getLocationLastMonthSeries = createSelector(
  getDashboardsState,
  dashboards.getLocationLastMonthSeries
)

export const getLocationReviewRequestTodaySeries = createSelector(
  getDashboardsState,
  dashboards.getLocationReviewRequestTodaySeries
)

export const getLocationReviewRequestLastWeekSeries = createSelector(
  getDashboardsState,
  dashboards.getLocationReviewRequestLastWeekSeries
)

export const getLocationReviewRequestLastMonthSeries = createSelector(
  getDashboardsState,
  dashboards.getLocationReviewRequestLastMonthSeries
)

export const getLocationSentVsPending = createSelector(
  getDashboardsState,
  dashboards.getLocationSentVsPending
)

export const getLocationFinishCampaign = createSelector(
  getDashboardsState,
  dashboards.getLocationFinishCampaign
)

export const getLastDayReviewCount = createSelector(
  getDashboardsState,
  dashboards.getLastDayReviewCount
)

export const getLastWeekReviewCount = createSelector(
  getDashboardsState,
  dashboards.getLastWeekReviewCount
)

export const getLastMonthReviewCount = createSelector(
  getDashboardsState,
  dashboards.getLastMonthReviewCount
)

export const getResellerBusinessCount = createSelector(
  getDashboardsState,
  dashboards.getresellerBusinessCount
)

export const getResellerUserCount = createSelector(
  getDashboardsState,
  dashboards.getresellerUserCount
)

export const getResellerLocationCount = createSelector(
  getDashboardsState,
  dashboards.getresellerLocationCount
)

export const getSiteAdminResellerCount = createSelector(
  getDashboardsState,
  dashboards.getSiteAdminResellerCount
)

export const getSiteAdminBusinessCount = createSelector(
  getDashboardsState,
  dashboards.getSiteAdminBusinessCount
)

export const getSiteAdminUserCount = createSelector(
  getDashboardsState,
  dashboards.getSiteAdminUserCount
)

export const getSiteAdminLocationCount = createSelector(
  getDashboardsState,
  dashboards.getSiteAdminLocationCount
)

export const getErrorDashboards = createSelector(
  getDashboardsState,
  dashboards.getError
);

/**
 * Reports Reducers
 */


export const getReportsState = createFeatureSelector<State, reports.State>(
  'reports'
);

export const getTotalGeneratedReports = createSelector(
  getReportsState,
  reports.getTotalGeneratedReports
)

export const getReportsSettingsByBusiness = createSelector(
  getReportsState,
  reports.getReportsSettings
)

export const getGeneratedReports = createSelector(
  getReportsState,
  reports.getGeneratedReports
)

export const getRatingPerLocation = createSelector(
  getReportsState,
  reports.getRating
)

export const getStarsCountPerLocation = createSelector(
  getReportsState,
  reports.getStarsCount
)

export const getReviewCountPerLocation = createSelector(
  getReportsState,
  reports.getReviewCount
)

export const getFeedbackCountPerLocation = createSelector(
  getReportsState,
  reports.getFeedbackCount
)

export const getClickedOnReviewCountPerLocation = createSelector(
  getReportsState,
  reports.getClickedOnReviewCount
)

export const getSendEngineEfficiencyPerLocation = createSelector(
  getReportsState,
  reports.getSendEngineEfficiency
)

export const getAverageContactSpeedPerLocation = createSelector(
  getReportsState,
  reports.getAvergaContactSpeed
)

export const getFeedbackReviewPercentagePerLocation = createSelector(
  getReportsState,
  reports.getFeedbackReviewPercentage
)

export const getReviewsPerReviewSitePerLocation = createSelector(
  getReportsState,
  reports.getReviewsPerReviewSites
)

export const getRatingPerReviewSitePerLocation = createSelector(
  getReportsState,
  reports.getRatingPerReviewSites
)

export const getSentRequestCountPerLocation = createSelector(
  getReportsState,
  reports.getSentRequestCount
)

export const getSentimentCheckCountPerLocation = createSelector(
  getReportsState,
  reports.getSentimentCheckCount
)

export const getCampaignsMetricsPerLocation = createSelector(
  getReportsState,
  reports.getCampaignMetrics
)

export const getGeneralReportDateRange = createSelector(
  getReportsState,
  reports.getGeneralReportDateRange
)

export const getScheduledReports = createSelector(
  getReportsState,
  reports.getScheduledReports
)

export const getErrorReports = createSelector(
  getReportsState,
  reports.getError
);

/**
 * Social Sharing
 */

export const getSocialSharingState = createFeatureSelector<State, facebook.State>(
  'facebook'
);

export const getSocialSharingPages = createSelector(
  getSocialSharingState,
  facebook.getPages
);

// TODO: which social sharing modal? needs better name
export const getIsSocialSharingModalOpen = createSelector(
  getSocialSharingState,
  facebook.getIsOpenModal
);

export const getFacebookAuthResult = createSelector(
  getSocialSharingState,
  facebook.getAuthToken
);

export const getSocialSharingScheduleSettings = createSelector(
  getSocialSharingState,
  facebook.getScheduleSettings
);

export const getSocialSharingTemplateSettings = createSelector(
  getSocialSharingState,
  facebook.getTemplateSettings
);

export const getIsSocialSharingExecuting = createSelector(
  getSocialSharingState,
  facebook.getIsExecutingTask
);

export const getSelectedFacebookTokens = createSelector(
  getSocialSharingState,
  facebook.getSelectedToken
)

export const getSocialSharingError = createSelector(
  getSocialSharingState,
  facebook.getError
);

export const getShareReviewSuccess = createSelector(
  getSocialSharingState,
  facebook.getShareReviewSuccess
);

export const getCommentToBePosted = createSelector(
  getSocialSharingState,
  facebook.getCommentToBePosted
);

export const getFacebookError = createSelector(
  getSocialSharingState,
  facebook.getFacebookError
);

/**
 * GMB
 */
export const getGMBState = createFeatureSelector<State, gmb.State>(
  'gmb'
);

export const getGMBError = createSelector(
  getGMBState,
  gmb.getError
);

export const getGMBBusinesses = createSelector(
  getGMBState,
  gmb.getBusinesses
);

export const getIsGMBExecuting = createSelector(
  getGMBState,
  gmb.getIsExecutingTask
);

/**
 * App Reducers
 */
export const getAppState = createFeatureSelector<State, app.State>(
  'app'
);

export const getIsTaskExecuting = createSelector(
  getAppState,
  app.getIsTaskExecuting
)
export const openSideDrawer = createSelector(
  getAppState,
  app.getOpenSideDrawer
)
export const getError = createSelector(
  getAppState,
  app.getError
)

export const getLastNotification = createSelector(
  getAppState,
  app.getLastNotification
)

export const getIsShowIntercom = createSelector(
  getAppState,
  app.getIsShowIntercom
)

export const getErrorTimeInactive = createSelector(
  getAppState,
  app.getErrorTimeInactive
)

export const getRedirectedFromGoogle = createSelector(
  getAppState,
  app.getRedirectedFromGoogle
)

export const getFacebookAuthState = createSelector(
  getSocialSharingState,
  facebook.getAuthState
);

export const getErrorTrialOver = createSelector(
  getAppState,
  app.getErrorTrialOver
)

//////////
export const getResellerBusinessLocation = createSelector(
  getSelectedResellerProfile,
  getSelectedBusinessProfile,
  getCurrentLocation,
  (reseller: ResellerModel, business: BusinessModel, location: LocationModel) => ({
    nameReseller: reseller.name,
    nameBusiness: business.name,
    nameLocation: location.name,
  })
);


/**
 * Reply Template Reducers
 */
export const getReplyTemplateState = createFeatureSelector<State, replyTemplate.State>(
  'replyTemplate'
);

export const getReplyTemplate = createSelector(
  getReplyTemplateState,
  replyTemplate.getReplyTemplate
);
